import * as _ from 'lodash';

import type { ButtonType } from '../../generic/Button';
import Button, { IconButton } from '../../generic/Button';
import { BUTTON_SIZE, BUTTON_TYPE } from '../../generic/Button/button-constants';
import { TextInput } from '../../generic/Forms';
import Icon, { GreenCheck, icons } from '../../generic/Icon';
import { ICON_SIZE } from '../../generic/Icon/icon-constants';
import Text from '../../generic/Text';
import Tooltip from '../../generic/Tooltip';
import { MAXIMUM_BUSINESS_SEATS, MINIMUM_BUSINESS_SEATS_STEP, toValidSeats } from '../../../../shared/subscriptions';

export enum SubTier {
  UNAUTHED = 0,
  FREE,
  LEGACY,
  BUSINESS,
  ENTERPRISE,
}

const COMPLIANCE_TOOLTIP_TEXT =
  'Scan and automatically identify, manage, and address open source licensing issues.';
const SECURITY_TOOLTIP_TEXT =
  'Prevent vulnerabilities from entering your codebase with curated vulnerability data.';
const QUALITY_TOOLTIP_TEXT = 'Ensure the quality of your dependencies with package health metrics.';

export type CurrentOrgSubscription = {
  tier: number;
  hasSecurity: boolean;
  devCount: number;
};

export type DevCountOptions = {
  devCount: number;
  onChange: (count: number) => void;
};

export type ToggleOptions = {
  isSelected: boolean;
  onToggle: () => void;
};

type SubscriptionTierButtonOptions = {
  onClick: () => Promise<void> | void;
  title: string;
  text?: string;
  type?: ButtonType;
};

type SubscriptionTierInfoProps = {
  name: string;
  panelTier: number;
  currentOrgSub: CurrentOrgSubscription;
  price?: string | number;
  featureList: string[];
  buttonOptions?: SubscriptionTierButtonOptions;
  securityOptions?: ToggleOptions;
  devCountOptions?: DevCountOptions;
};

type SecurityPillProps = Pick<
  SubscriptionTierInfoProps,
  'panelTier' | 'securityOptions'
> & {
  tooltipText: string;
};

type QualityPillProps = Pick<SubscriptionTierInfoProps, 'panelTier'> & {
  tooltipText: string;
};

type DevCountPillProps = Pick<
  SubscriptionTierInfoProps,
  'panelTier' | 'devCountOptions'
>;
type SelectableDevCountPillProps = Required<
  Pick<DevCountPillProps, 'devCountOptions'>
>;

type SubscriptionTierButtonProps = Pick<
  SubscriptionTierInfoProps,
  'currentOrgSub' | 'panelTier' | 'buttonOptions'
> & {
  selectedDevCount?: number;
  isSecuritySelected: boolean;
};

type FeaturePillProps = {
  text: string;
  tooltipText: string;
  feature: string;
  isToggleable?: boolean;
  toggleOptions?: ToggleOptions;
};

const FeaturePill = ({
  text,
  tooltipText,
  feature,
  toggleOptions,
  isToggleable = false,
}: FeaturePillProps) => {
  const shouldShowToggle = isToggleable && toggleOptions !== undefined;

  // If we're making a Toggleable pill, use the state value. Otherwise, default to true.
  const isSelected = shouldShowToggle ? toggleOptions.isSelected : true;

  const leftIcon = shouldShowToggle ? (
    <Icon
      size={ICON_SIZE.LG}
      icon={toggleOptions.isSelected ? icons.minus : icons.add}
    />
  ) : (
    <Icon size={ICON_SIZE.LG} icon={icons.check} />
  );

  return (
    <div
      className={`sub-tier-info__feature-pill sub-tier-info__feature-pill--${feature} ${
        isSelected ? `sub-tier-info__feature-pill--${feature}--selected` : ''
      }`}
      onClick={shouldShowToggle ? toggleOptions.onToggle : undefined}
      onKeyPress={shouldShowToggle ? toggleOptions.onToggle : undefined}
      role="button"
      tabIndex={0}
    >
      <span className={`sub-tier-info__feature-pill--${feature}__icon`}>
        {leftIcon}
      </span>
      <span className="sub-tier-info__feature-pill__text">{text}</span>
      <Tooltip tooltip={tooltipText}>
        <span className={`sub-tier-info__feature-pill--${feature}_icon`}>
          <Icon size={ICON_SIZE.LG} icon={icons.infoCircle} />
        </span>
      </Tooltip>
    </div>
  );
};

const CurrentPlanButton = () => (
  <Button
    isFullWidth
    isDisabled
    type="secondary"
    size={BUTTON_SIZE.LARGE}
    onClick={Promise.resolve}
  >
    Current Plan
  </Button>
);

const SubscriptionTierButton = ({
  currentOrgSub,
  panelTier,
  selectedDevCount,
  isSecuritySelected,
  buttonOptions,
}: SubscriptionTierButtonProps) => {
  // Short circuit if it's an unauthed user.
  if (currentOrgSub.tier === SubTier.UNAUTHED && panelTier !== SubTier.ENTERPRISE) {
    const buttonType = panelTier === SubTier.FREE ? BUTTON_TYPE.PRIMARY : BUTTON_TYPE.DARK;
    return (
      <Button
        isFullWidth
        size={BUTTON_SIZE.LARGE}
        type={buttonType}
        onClick={() => {
          window.location.pathname = '/auth/register';
        }}
      >
        {panelTier === SubTier.FREE ? 'Start for Free' : 'Get Started'}
      </Button>
    );
  }

  // We define this up here so that we can override for Business tier security toggling and devCount changing.
  let buttonText = buttonOptions?.text;
  if (!buttonText) {
    buttonText = currentOrgSub.tier > panelTier ? 'Downgrade' : 'Upgrade';
  }

  if (currentOrgSub.tier === panelTier) {
    switch (currentOrgSub.tier) {
      case SubTier.BUSINESS: {
        // We need to know the number of devs selected.
        if (selectedDevCount !== undefined) {
          // Business tier can still upgrade/downgrade from security feature toggling. We want to display "Current Plan"
          // when Security hasn't been adjusted.
          if (
            currentOrgSub.hasSecurity === isSecuritySelected &&
            currentOrgSub.devCount === selectedDevCount
          ) {
            return <CurrentPlanButton />;
          }

          if (currentOrgSub.hasSecurity !== isSecuritySelected) {
            // If we've gotten this far, it means they might remove/add security from their Business subscription.
            // We want to display "Downgrade" when they already have security, "Upgrade" when they don't.
            buttonText = currentOrgSub.hasSecurity ? 'Downgrade' : 'Upgrade';
          } else {
            // The dev count is different. Display "Upgrade" when more devs selected, "Downgrade" when less.
            buttonText =
              currentOrgSub.devCount > selectedDevCount
                ? 'Downgrade'
                : 'Upgrade';
          }
        }
        break;
      }
      default:
        return <CurrentPlanButton />;
    }
  }

  // At this point, we need button options to build the final button.
  if (!buttonOptions) {
    return null;
  }

  return (
    <Button
      isFullWidth
      size={BUTTON_SIZE.LARGE}
      type={buttonOptions.type || BUTTON_TYPE.PRIMARY}
      onClick={buttonOptions.onClick}
    >
      {buttonText}
    </Button>
  );
};

const SecurityPill = ({
  panelTier,
  tooltipText,
  securityOptions,
}: SecurityPillProps) => {
  switch (panelTier) {
    case SubTier.FREE:
      return <FeaturePill text="Limited Security" tooltipText={tooltipText} feature="security" />;
    case SubTier.BUSINESS: {
      if (!securityOptions) {
        return null;
      }

      return (
        <FeaturePill
          text="Security Vulnerabilities"
          tooltipText={tooltipText}
          feature="security"
          isToggleable
          toggleOptions={securityOptions}
        />
      );
    }
    case SubTier.ENTERPRISE:
      return <FeaturePill text="Security Vulnerabilities" tooltipText={tooltipText} feature="security" />;
    default:
      return null;
  }
};

const QualityPill = ({ panelTier, tooltipText }: QualityPillProps) => {
  switch (panelTier) {
    case SubTier.FREE:
    case SubTier.BUSINESS:
      return <FeaturePill text="Limited Quality" tooltipText={tooltipText} feature="quality" />;
    case SubTier.ENTERPRISE:
      return <FeaturePill text="Package Quality" tooltipText={tooltipText} feature="quality" />;
    default:
      return null;
  }
};

const SelectableDevCountPill = ({ devCountOptions: { devCount, onChange } }: SelectableDevCountPillProps) => {
  const validateBeforeOnChange = (newValue: number | string) => onChange(toValidSeats(newValue));

  return (
    <div className="sub-tier-info__feature-pill sub-tier-info__feature-pill--selectable-dev-count">
      <span className="sub-tier-info__feature-pill--selectable-dev-count--input">
        <TextInput
          label="Selected Contributors"
          value={devCount.toString()}
          size="medium"
          onChange={validateBeforeOnChange}
        />{' '}
        CODE CONTRIBUTOR{devCount === 1 ? '' : 'S'}
      </span>
      {/* TODO: Turn this pair of IconButtons into a generic component. Might be useful? */}
      <span className="sub-tier-info__feature-pill--selectable-dev-count--controls padding-right-sm">
        <IconButton
          id="increase-contributor"
          icon={icons.caretUp}
          onClick={() => validateBeforeOnChange(devCount + MINIMUM_BUSINESS_SEATS_STEP)}
        />
        <IconButton
          id="decrease-contributor"
          icon={icons.caretDown}
          onClick={() => validateBeforeOnChange(devCount - MINIMUM_BUSINESS_SEATS_STEP)}
        />
      </span>
    </div>
  );
};

const DevCountPill = ({ panelTier, devCountOptions }: DevCountPillProps) => {
  switch (panelTier) {
    case SubTier.FREE:
      return null;
    case SubTier.BUSINESS: {
      if (!devCountOptions) {
        return null;
      }

      return <SelectableDevCountPill devCountOptions={devCountOptions} />;
    }
    case SubTier.ENTERPRISE:
      return (
        <div className="sub-tier-info__feature-pill sub-tier-info__feature-pill--selectable-dev-count">
          {MAXIMUM_BUSINESS_SEATS}+ CODE CONTRIBUTORS
        </div>
      );
    default:
      return null;
  }
};

/**
 * Component to display Subscription Tier information and upgrade selection.
 *
 * @param {SubscriptionTierInfoProps} props - The props for the component.
 * @param {string} props.name - The name of the tier to display
 * @param {SubTier} props.panelTier - The tier that the component is displaying information for.
 * @param {SubTier} props.currentOrgSub.tier - The tier for the current user's organization.
 * @param {SubTier} props.currentOrgSub.hasSecurity - Whether or not the organization is already paying for Security.
 * @param {string | number} props.price - The text to display for the price.
 * @param {string[]} props.featureList - The list of features to display for the subscription tier.
 * @param {(tier: SubTier) => void | Promise<void>} props.buttonOptions.onClick - The function to run when the button is clicked for the tier.
 *                                                 Passes in the panel's SubTier value.
 *                                                 Will not be called if the user's current tier is the same as the tier for the component.
 * @param {string} props.buttonOptions.title - The title to assign to the component's button
 * @param {string} props.buttonOptions.text - The text to display on the button. This will override the default "Upgrade"/"Downgrade"
 *                               text, but not the "Current Plan" text if the component tier is the same as the user's tier.
 * @param {boolean} props.securityOptions.isSelected - Used for Business plan security feature toggling. Comes from the parent component's state.
 * @param {() => void} props.securityOptions.onToggle - The function to run when the security feature is toggled. Comes from the parent component's state.
 * @param {number} props.devCountOptions.devCount - The number of developers selected for Business tier. Comes from the parent component's state.
 * @param {(count: number) => void} props.devCountOptions.onChange - The function to run when the number of developers changes. Comes from the parent component's state.
 * @returns The SubscriptionTierInfo component.
 */
export const SubscriptionTierInfo = ({
  name,
  panelTier,
  currentOrgSub,
  price,
  featureList,
  buttonOptions,
  securityOptions,
  devCountOptions,
}: SubscriptionTierInfoProps) => {
  const featureListContents = featureList.map((feature) => (
    <div
      className="sub-tier-info__feature padding-top-xs padding-bottom-xs"
      key={feature}
    >
      <span className="padding-right-sm">
        <GreenCheck size={ICON_SIZE.LG} />
      </span>
      <span className="details">{feature}</span>
    </div>
  ));

  return (
    <div className="sub-tier-info">
      <div className="sub-tier-info__title">
        <Text.H1>{name}</Text.H1>
      </div>
      <div className="feature-pills">
        <FeaturePill text="License Compliance" tooltipText={COMPLIANCE_TOOLTIP_TEXT} feature="licensing" />
        <SecurityPill panelTier={panelTier} tooltipText={SECURITY_TOOLTIP_TEXT} securityOptions={securityOptions} />
        <QualityPill panelTier={panelTier} tooltipText={QUALITY_TOOLTIP_TEXT} />
        <DevCountPill panelTier={panelTier} devCountOptions={devCountOptions} />
      </div>
      <span className="pricing padding-bottom-sm">
        {!_.isNil(price) && <Text.H2>{_.isNumber(price) ? `$${price}/mo` : price}</Text.H2>}
      </span>
      <div className="padding-bottom-sm">{featureListContents}</div>
      <span className="sub-tier-info__button">
        <SubscriptionTierButton
          currentOrgSub={currentOrgSub}
          panelTier={panelTier}
          isSecuritySelected={!!securityOptions?.isSelected}
          buttonOptions={buttonOptions}
          selectedDevCount={devCountOptions?.devCount}
        />
      </span>
    </div>
  );
};
